/**
 * Content validation (content-schema §1, §5, §7, §10; tech-design §3).
 *
 * Two passes, both pure and headless (no I/O — the CLI and the Vitest suite feed
 * it already-parsed JSON):
 *
 *  1. **Schema** — every pack must satisfy its Zod schema (shape, closed-vocabulary
 *     curves/effects/conditions, Decimal-safe amounts).
 *  2. **Referential integrity** — ids are globally unique, every referenced id
 *     resolves, every effect target resolves against the registry *and* its
 *     entity, and every age has a reachable breakthrough goal.
 *
 * Every {@link ContentError} names the pack, the offending entity id, and the
 * field — so a failure is self-explanatory to a human or an agent (§10 acceptance).
 */
import type { z } from 'zod';
import { GlobalPackSchema, PackSchema } from './schemas/entities.js';
import type {
  Age,
  Breakthrough,
  Endgame,
  Generator,
  GlobalPack,
  Milestone,
  PrestigeLayer,
  Resource,
  Trait,
  Upgrade,
} from './schemas/entities.js';
import type { Condition } from './schemas/primitives.js';
import { parseEffectTarget } from './schemas/primitives.js';

/** A single validation failure, always naming pack + entity + field. */
export interface ContentError {
  readonly pack: string;
  readonly id: string;
  readonly field: string;
  readonly message: string;
}

/** One input pack: a name (for error messages) and its parsed-from-JSON data. */
export interface RawPack {
  readonly name: string;
  readonly data: unknown;
  /** `true` for the `global.json` pack (prestige layers), which uses a different schema. */
  readonly global?: boolean;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Best-effort id for a schema issue: walk the issue path and return the id of the
 * nearest enclosing object that has a string `id` — so even a shape error points
 * at the entity it belongs to.
 */
function nearestId(root: unknown, path: readonly (string | number)[]): string {
  let node: unknown = root;
  let id = '(pack)';
  const idOf = (n: unknown): string | undefined =>
    isRecord(n) && typeof n.id === 'string' ? n.id : undefined;

  const rootId = idOf(node);
  if (rootId !== undefined) id = rootId;

  for (const key of path) {
    if (isRecord(node)) {
      node = node[key];
    } else if (Array.isArray(node) && typeof key === 'number') {
      node = node[key];
    } else {
      break;
    }
    const here = idOf(node);
    if (here !== undefined) id = here;
  }
  return id;
}

interface ParsedPack {
  readonly name: string;
  readonly age: Age;
  readonly resources: readonly Resource[];
  readonly generators: readonly Generator[];
  readonly upgrades: readonly Upgrade[];
  readonly traits: readonly Trait[];
  readonly breakthroughs: readonly Breakthrough[];
  readonly milestones: readonly Milestone[];
  readonly endgame?: Endgame | undefined;
}

/** Runs the full schema + referential-integrity pipeline over every pack. */
export function validateContent(packs: readonly RawPack[]): ContentError[] {
  const errors: ContentError[] = [];
  const agePacks: ParsedPack[] = [];
  const globalPacks: { name: string; data: GlobalPack }[] = [];

  // ── Pass 1: schema ──────────────────────────────────────────────────────────
  for (const raw of packs) {
    if (raw.global === true) {
      const result = GlobalPackSchema.safeParse(raw.data);
      if (result.success) {
        globalPacks.push({ name: raw.name, data: result.data });
      } else {
        pushZodErrors(errors, raw, result.error.issues);
      }
      continue;
    }
    const result = PackSchema.safeParse(raw.data);
    if (result.success) {
      agePacks.push({ name: raw.name, ...result.data });
    } else {
      pushZodErrors(errors, raw, result.error.issues);
    }
  }

  // ── Pass 2: referential integrity ─────────────────────────────────────────────
  const registry = buildRegistry(agePacks, globalPacks, errors);
  checkAgePackReferences(agePacks, registry, errors);
  checkGlobalReferences(globalPacks, registry, errors);

  return errors;
}

function pushZodErrors(
  errors: ContentError[],
  raw: RawPack,
  issues: readonly z.ZodIssue[],
): void {
  for (const issue of issues) {
    errors.push({
      pack: raw.name,
      id: nearestId(raw.data, issue.path),
      field: issue.path.length > 0 ? issue.path.join('.') : '(root)',
      message: issue.message,
    });
  }
}

interface Registry {
  readonly ages: Set<string>;
  readonly resources: Set<string>;
  readonly generators: Set<string>;
  readonly upgrades: Set<string>;
  readonly traits: Set<string>;
  readonly breakthroughs: Set<string>;
  readonly milestones: Set<string>;
  readonly clickActions: Set<string>;
  readonly prestige: Set<string>;
  /** breakthrough id → whether its reward completes the age. */
  readonly breakthroughCompletesAge: Map<string, boolean>;
}

function buildRegistry(
  agePacks: readonly ParsedPack[],
  globalPacks: readonly { name: string; data: GlobalPack }[],
  errors: ContentError[],
): Registry {
  const ages = new Set<string>();
  const resources = new Set<string>();
  const generators = new Set<string>();
  const upgrades = new Set<string>();
  const traits = new Set<string>();
  const breakthroughs = new Set<string>();
  const milestones = new Set<string>();
  const clickActions = new Set<string>();
  const prestige = new Set<string>();
  const breakthroughCompletesAge = new Map<string, boolean>();

  const seen = new Set<string>();
  const claim = (pack: string, id: string, field: string, into: Set<string>): void => {
    if (seen.has(id)) {
      errors.push({
        pack,
        id,
        field,
        message: `duplicate id "${id}" (ids must be globally unique)`,
      });
    }
    seen.add(id);
    into.add(id);
  };

  // Background sprite assets share the global id-space so the art pipeline's
  // one-file-per-id output can never collide, but nothing references them — they
  // only need uniqueness, not a registry entry.
  const backgroundAssets = new Set<string>();

  for (const p of agePacks) {
    claim(p.name, p.age.id, 'age.id', ages);
    claim(p.name, p.age.clickAction.id, 'age.clickAction.id', clickActions);
    if (p.age.background !== undefined) {
      for (const sprite of p.age.background.sprites)
        claim(p.name, sprite.id, 'age.background.sprites[].id', backgroundAssets);
    }
    for (const r of p.resources) claim(p.name, r.id, 'resources[].id', resources);
    for (const g of p.generators) claim(p.name, g.id, 'generators[].id', generators);
    for (const u of p.upgrades) claim(p.name, u.id, 'upgrades[].id', upgrades);
    for (const t of p.traits) claim(p.name, t.id, 'traits[].id', traits);
    for (const b of p.breakthroughs) {
      claim(p.name, b.id, 'breakthroughs[].id', breakthroughs);
      breakthroughCompletesAge.set(b.id, b.rewards.ageComplete === true);
    }
    for (const m of p.milestones) claim(p.name, m.id, 'milestones[].id', milestones);
  }
  for (const gp of globalPacks) {
    for (const layer of gp.data.prestige) {
      claim(gp.name, layer.id, 'prestige[].id', prestige);
      // Tree upgrades share the upgrade id-space so `upgradeOwned` unlocks resolve.
      for (const upgrade of layer.tree)
        claim(gp.name, upgrade.id, 'prestige[].tree[].id', upgrades);
    }
    for (const m of gp.data.milestones)
      claim(gp.name, m.id, 'milestones[].id', milestones);
  }

  return {
    ages,
    resources,
    generators,
    upgrades,
    traits,
    breakthroughs,
    milestones,
    clickActions,
    prestige,
    breakthroughCompletesAge,
  };
}

function checkAgePackReferences(
  agePacks: readonly ParsedPack[],
  reg: Registry,
  errors: ContentError[],
): void {
  for (const p of agePacks) {
    const ref = (
      id: string,
      into: Set<string>,
      entity: string,
      field: string,
      kind: string,
    ): void => {
      if (!into.has(id)) {
        errors.push({
          pack: p.name,
          id: entity,
          field,
          message: `references unknown ${kind} "${id}"`,
        });
      }
    };

    // Age art direction: optional in the Zod schema (so minimal engine-test packs
    // stay lean) but mandatory for every shipped age — each age needs a 9-slice UI
    // frame style and a parallax backdrop for the art pipeline to generate.
    if (p.age.uiPrompt === undefined) {
      errors.push({
        pack: p.name,
        id: p.age.id,
        field: 'uiPrompt',
        message:
          'age is missing its UI style prompt (uiPrompt drives the 9-slice frame set)',
      });
    }
    if (p.age.background === undefined) {
      errors.push({
        pack: p.name,
        id: p.age.id,
        field: 'background',
        message:
          'age is missing its parallax background (backdrop prompt + z-layered sprites)',
      });
    }

    // Age
    ref(
      p.age.goal.breakthrough,
      reg.breakthroughs,
      p.age.id,
      'goal.breakthrough',
      'breakthrough',
    );
    if (
      reg.breakthroughs.has(p.age.goal.breakthrough) &&
      reg.breakthroughCompletesAge.get(p.age.goal.breakthrough) !== true
    ) {
      errors.push({
        pack: p.name,
        id: p.age.id,
        field: 'goal.breakthrough',
        message: `goal breakthrough "${p.age.goal.breakthrough}" is not a reachable age goal (its rewards must set ageComplete: true)`,
      });
    }
    for (const grant of p.age.clickAction.grants) {
      ref(
        grant.resource,
        reg.resources,
        p.age.clickAction.id,
        'clickAction.grants[].resource',
        'resource',
      );
    }
    for (const exp of p.age.completionExport ?? []) {
      ref(
        exp.resource,
        reg.resources,
        p.age.id,
        'completionExport[].resource',
        'resource',
      );
    }
    if (p.age.unlock)
      checkCondition(p.age.unlock, reg, p.name, p.age.id, 'unlock', errors);

    // Resources
    for (const r of p.resources) ref(r.age, reg.ages, r.id, 'age', 'age');

    // Generators
    for (const g of p.generators) {
      ref(g.age, reg.ages, g.id, 'age', 'age');
      ref(g.cost.resource, reg.resources, g.id, 'cost.resource', 'resource');
      for (const y of g.produces)
        ref(y.resource, reg.resources, g.id, 'produces[].resource', 'resource');
      for (const y of g.consumes ?? [])
        ref(y.resource, reg.resources, g.id, 'consumes[].resource', 'resource');
      if (g.unlock) checkCondition(g.unlock, reg, p.name, g.id, 'unlock', errors);
    }

    // Upgrades
    for (const u of p.upgrades) {
      ref(u.age, reg.ages, u.id, 'age', 'age');
      ref(u.cost.resource, reg.resources, u.id, 'cost.resource', 'resource');
      checkEffects(u.effects, reg, p.name, u.id, errors);
      if (u.unlock) checkCondition(u.unlock, reg, p.name, u.id, 'unlock', errors);
    }

    // Traits
    for (const t of p.traits) {
      ref(t.age, reg.ages, t.id, 'age', 'age');
      checkEffects(t.effects, reg, p.name, t.id, errors);
      if (t.unlock) checkCondition(t.unlock, reg, p.name, t.id, 'unlock', errors);
    }

    // Breakthroughs
    for (const b of p.breakthroughs) {
      ref(b.age, reg.ages, b.id, 'age', 'age');
      for (const c of b.cost)
        ref(c.resource, reg.resources, b.id, 'cost[].resource', 'resource');
      for (const entry of b.rewards.pool ?? []) {
        ref(entry.trait, reg.traits, b.id, 'rewards.pool[].trait', 'trait');
      }
      if (b.unlock) checkCondition(b.unlock, reg, p.name, b.id, 'unlock', errors);
    }

    // Milestones
    for (const m of p.milestones) {
      checkCondition(m.condition, reg, p.name, m.id, 'condition', errors);
      checkEffects(m.effects, reg, p.name, m.id, errors);
    }

    // Endgame (GAM-24) — only the final age ships one.
    if (p.endgame !== undefined) {
      checkEndgame(p.endgame, reg, p.name, errors);
    }
  }
}

/** Resolve the endgame block's cross-references: the ceremony's triggering
 * breakthrough and prestige layer, each stat's layer/currency, and each content
 * branch's Essence currency (a prestige-layer id) + effect targets (GAM-24). */
function checkEndgame(
  endgame: Endgame,
  reg: Registry,
  pack: string,
  errors: ContentError[],
): void {
  const { ceremony } = endgame;
  if (!reg.breakthroughs.has(ceremony.triggeredBy)) {
    errors.push({
      pack,
      id: ceremony.id,
      field: 'ceremony.triggeredBy',
      message: `references unknown breakthrough "${ceremony.triggeredBy}"`,
    });
  }
  if (!reg.prestige.has(ceremony.prestige)) {
    errors.push({
      pack,
      id: ceremony.id,
      field: 'ceremony.prestige',
      message: `references unknown prestige layer "${ceremony.prestige}"`,
    });
  }
  for (const stat of endgame.stats) {
    const { source } = stat;
    const layerRef =
      source.type === 'prestigeCount'
        ? source.layer
        : source.type === 'lifetimeCurrency'
          ? source.currency
          : source.type === 'recordRunDuration'
            ? source.event
            : undefined;
    if (layerRef !== undefined && !reg.prestige.has(layerRef)) {
      errors.push({
        pack,
        id: stat.id,
        field: 'source',
        message: `references unknown prestige layer "${layerRef}"`,
      });
    }
  }
  for (const branch of endgame.contentBranches) {
    for (const c of branch.cost) {
      if (!reg.prestige.has(c.currency)) {
        errors.push({
          pack,
          id: branch.id,
          field: 'cost[].currency',
          message: `references unknown prestige layer "${c.currency}"`,
        });
      }
    }
    checkEffects(branch.effects, reg, pack, branch.id, errors);
  }
}

function checkGlobalReferences(
  globalPacks: readonly { name: string; data: GlobalPack }[],
  reg: Registry,
  errors: ContentError[],
): void {
  for (const gp of globalPacks) {
    for (const layer of gp.data.prestige) {
      checkPrestigeLayer(layer, reg, gp.name, errors);
    }
    for (const m of gp.data.milestones) {
      checkCondition(m.condition, reg, gp.name, m.id, 'condition', errors);
      checkEffects(m.effects, reg, gp.name, m.id, errors);
    }
  }
}

function checkPrestigeLayer(
  layer: PrestigeLayer,
  reg: Registry,
  pack: string,
  errors: ContentError[],
): void {
  const metric = layer.gain.metric;
  // The ageWeighted metric (GAM-11) references no ids; only resourceLifetime does.
  if (metric.type === 'resourceLifetime') {
    if (metric.resource !== undefined && !reg.resources.has(metric.resource)) {
      errors.push({
        pack,
        id: layer.id,
        field: 'gain.metric.resource',
        message: `references unknown resource "${metric.resource}"`,
      });
    }
    for (const [ageId, resourceId] of Object.entries(metric.resourcePerAge ?? {})) {
      if (!reg.ages.has(ageId)) {
        errors.push({
          pack,
          id: layer.id,
          field: 'gain.metric.resourcePerAge',
          message: `references unknown age "${ageId}"`,
        });
      }
      if (!reg.resources.has(resourceId)) {
        errors.push({
          pack,
          id: layer.id,
          field: 'gain.metric.resourcePerAge',
          message: `references unknown resource "${resourceId}"`,
        });
      }
    }
  }
  for (const ageId of Object.keys(layer.currency.names ?? {})) {
    if (!reg.ages.has(ageId)) {
      errors.push({
        pack,
        id: layer.id,
        field: 'currency.names',
        message: `references unknown age "${ageId}"`,
      });
    }
  }
  // Prestige-tree upgrades are costed in the layer currency (not a resource id),
  // so only their effect targets and unlock conditions are resolved here.
  for (const upgrade of layer.tree) {
    checkEffects(upgrade.effects, reg, pack, upgrade.id, errors);
    if (upgrade.unlock !== undefined) {
      checkCondition(upgrade.unlock, reg, pack, upgrade.id, 'unlock', errors);
    }
  }
}

function checkEffects(
  effects: readonly { target: string }[],
  reg: Registry,
  pack: string,
  entityId: string,
  errors: ContentError[],
): void {
  for (const effect of effects) {
    const parsed = parseEffectTarget(effect.target);
    // Shape already validated by the schema; id-less targets (offline.cap) need no ref.
    if (parsed === null) continue;
    if (parsed.id === null || parsed.ref === null) continue;
    const set = refSetFor(parsed.ref, reg);
    if (!set.has(parsed.id)) {
      errors.push({
        pack,
        id: entityId,
        field: 'effects[].target',
        message: `effect target references unknown ${parsed.ref} "${parsed.id}"`,
      });
    }
  }
}

function refSetFor(
  ref:
    | 'generator'
    | 'resource'
    | 'breakthrough'
    | 'age'
    | 'clickAction'
    | 'prestige'
    | 'trait',
  reg: Registry,
): Set<string> {
  switch (ref) {
    case 'generator':
      return reg.generators;
    case 'resource':
      return reg.resources;
    case 'breakthrough':
      return reg.breakthroughs;
    case 'age':
      return reg.ages;
    case 'clickAction':
      return reg.clickActions;
    case 'prestige':
      return reg.prestige;
    case 'trait':
      return reg.traits;
  }
}

function checkCondition(
  condition: Condition,
  reg: Registry,
  pack: string,
  entityId: string,
  field: string,
  errors: ContentError[],
): void {
  const bad = (kind: string, id: string): void => {
    errors.push({
      pack,
      id: entityId,
      field,
      message: `unlock condition references unknown ${kind} "${id}"`,
    });
  };
  const walk = (c: Condition): void => {
    if ('all' in c) {
      for (const sub of c.all) walk(sub);
      return;
    }
    if ('any' in c) {
      for (const sub of c.any) walk(sub);
      return;
    }
    if ('not' in c) {
      walk(c.not);
      return;
    }
    switch (c.type) {
      case 'resourceLifetime':
      case 'resourceCurrent':
        if (!reg.resources.has(c.resource)) bad('resource', c.resource);
        return;
      case 'generatorOwned':
        if (!reg.generators.has(c.generator)) bad('generator', c.generator);
        return;
      case 'upgradeOwned':
        if (!reg.upgrades.has(c.upgrade)) bad('upgrade', c.upgrade);
        return;
      case 'traitOwned':
        if (!reg.traits.has(c.trait)) bad('trait', c.trait);
        return;
      case 'milestone':
        if (!reg.milestones.has(c.milestone)) bad('milestone', c.milestone);
        return;
      case 'breakthroughHits':
        if (!reg.breakthroughs.has(c.breakthrough)) bad('breakthrough', c.breakthrough);
        return;
      case 'ageCompleted':
        if (!reg.ages.has(c.age)) bad('age', c.age);
        return;
      case 'prestigeCount':
        if (!reg.prestige.has(c.layer)) bad('prestige', c.layer);
        return;
    }
  };
  walk(condition);
}

/** True when a pack set is valid (no errors) — a convenience for load-time guards. */
export function isContentValid(packs: readonly RawPack[]): boolean {
  return validateContent(packs).length === 0;
}

/** Format errors as one line each: `pack › id › field: message`. */
export function formatContentErrors(errors: readonly ContentError[]): string {
  return errors.map((e) => `  ${e.pack} › ${e.id} › ${e.field}: ${e.message}`).join('\n');
}
